import Phaser from "phaser"
import { calculateScaleFactor } from "../config/scaling"
import {
	updatePlanetData,
	checkLocalSavedData,
	playUISound,
	handleGameProgressUpdate,
	saveCompletedAndUnlockedPlanets,
	animateElements,
	displayInstructions,
	playExplosionAnimation,
} from "../helpers/levelManager"
import { GameAnalytics } from "gameanalytics"
import { MediclinicAPI } from "../helpers/apiHandler"

export default class MissionOutro extends Phaser.Scene {
	constructor() {
		super({ key: "MissionOutro" })
		this.alertContentGroup = null
		this.alertsGroup = null
		this.pageIndicators = []
	}

	init() {
		// Initialize any properties needed
		this.savedUserData = null
		this.savedLevel = 0
		this.savedMiniLevel = 0

		// Detect iOS devices
		this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

		this.gameW = this.cameras.main.width
		this.gameH = this.cameras.main.height

		const scalingData = calculateScaleFactor(this)
		this.scaleFactor = scalingData.scaleFactor
		this.safeZoneX = scalingData.safeZoneX
		this.safeZoneY = scalingData.safeZoneY
		this.safeZoneWidth = scalingData.safeZoneWidth
		this.safeZoneHeight = scalingData.safeZoneHeight

		// Responsive font sizing
		this.baseFontSize = 20
		this.fontSize = this.baseFontSize * this.scaleFactor

		this.loadSavedData()

		this.missionPanelIndex = 0

		this.apiHandler = new MediclinicAPI()

		this.learningOutcomesJson = this.cache.json.get("learningOutcomes")
		this.learningOutcomesData = this.learningOutcomesJson.learningOutcomes
		console.log("Learning Outcomes Data: " + this.learningOutcomesData[1].outcome)

		this.openedOutcomes = new Set()
		this.outcomesOpened = 0

		// Map learning outcome text to video filenames
		this.videoMap = {
			"Clicking/Tapping": { file: "clicking-tapping", type: "video" },
			"Disabled elements": { file: "disabled", type: "video" },
			"Opening/closing programs": { file: "open-close program", type: "video" },
			"Navigation Buttons": { file: "nav buttons", type: "video" },
			"Checkboxes": { file: "checkboxes", type: "video" },
			"Vertical scrolling": { file: "vertical scroll", type: "video" },
			"Forms": { file: "forms", type: "image" },
			"Tabs": { file: "tabs", type: "video" },
			"Saving data": { file: "saving", type: "video" },
			"Entering numbers & letters": { file: "entering", type: "image" },
			"Searching": { file: "searching", type: "video" },
			"Required fields": { file: "required fields", type: "image" },
			"Logging in/out": { file: "log in out", type: "video" },
			"Opening/closing windows": { file: "open-close program", type: "video" },
			"Email": { file: "email", type: "image" },
			"Toggle": { file: "toggle", type: "video" },
			"Dropdowns": { file: "dropdown", type: "video" },
			"Backspace/delete": { file: "backspace", type: "video" },
			"Radio buttons": { file: "radio buttons", type: "video" },
			"Logging time": { file: "logging time", type: "image" },
			"Horizontal scrolling": { file: "horizontal scroll", type: "video" },
			"A mouse": { file: "mouse", type: "image" },
			"A printer": { file: "printer", type: "image" },
			"Burger menus": { file: "burger menu", type: "video" },
			"Shutting down": { file: "shut down", type: "video" },
			"The power button": { file: "power button", type: "video" },
			"Starting up": { file: "start up", type: "video" },
			"Zooming in/out": { file: "zoom in-out", type: "video" },
			"Scanning a barcode": { file: "scan barcode", type: "video" },
			"Wi-Fi": { file: "wifi", type: "image" },
			"No Internet connection": { file: "no internet connection", type: "video" },
			"Opening a link": { file: "open link", type: "video" },
			"Entering special characters": { file: "special character", type: "video" },
			"Entering uppercase & lowercase letters": { file: "upperlower case", type: "video" },
			"Drag & drop": { file: "drag n drop", type: "video" },
			"Laptops": { file: "laptops", type: "image" },
			"Desktops": { file: "desktops", type: "image" },
			"One time passwords(OTP)": { file: "otp", type: "image" },
			"Authentication links": { file: "auth link", type: "video" },
			"Help desk": { file: "help desk", type: "image" },
			"Navigation bars": { file: "nav bar", type: "video" }
		};
		// Track loaded videos to avoid reloading
		this.loadedVideos = new Set();
		this.currentVideo = null;
	}

	loadSavedData() {
		this.savedUserData = checkLocalSavedData()
		this.currentPlanet = this.savedUserData.currentPlanet
		this.userID = this.savedUserData.userID
		this.savedMiniLevel = this.savedUserData.userMiniLevel
		//console.log("Current Planet: " + this.currentPlanet);
	}

	create() {
		this.soundManager = this.scene.get("GameUI").soundManager
		this.sendAnalyticsEvent()
		this.levelCompleteMileStone()
		this.playerGameRating()

		// Fade in camera
		this.cameras.main.fadeIn(500)

		let blackBackground = this.add.graphics()
		blackBackground.fillStyle(0x000000, 1)
		blackBackground.fillRect(0, 0, this.gameW, this.gameH)

		let background = this.add
			.image(this.gameW / 2, this.gameH / 2, "spaceBG2")
			.setOrigin(0.5, 0.5)
			.setScale(this.scaleFactor * 0.5)
			.setAlpha(0.63)

		let learntData = []
		let currentPlanetName
		this.learntDataLength = 0

		const planetData = {
			1: {
				name: "Hypotherra",
				learntData: [
					"Clicking/Tapping",
					"Disabled elements",
					"Opening/closing programs",
					"Navigation Buttons",
					"Checkboxes",
					"Vertical scrolling",
					"Forms",
					"Tabs",
					"Saving data",
					"Entering numbers & letters",
					"Searching",
					"Required fields",
					"Logging in/out",
					"Opening/closing windows",
					"Email",
					"Toggle",
				],
			},
			2: {
				name: "Stratosoma",
				learntData: [
					"Dropdowns",
					"Backspace/delete",
					"Radio buttons",
					"Logging time",
					"Horizontal scrolling",
					"A mouse",
					"A printer",
					"Burger menus",
					"Shutting down",
					"The power button",
					"Starting up",
				],
			},
			3: {
				name: "Emberon",
				learntData: [
					"Zooming in/out",
					"Scanning a barcode",
					"Wi-Fi",
					//"Internet",
					"No Internet connection",
					"Opening a link",
					"Entering special characters",
					"Entering uppercase & lowercase letters",
					"Drag & drop",
					"Laptops",
					"Desktops",
					"One time passwords(OTP)",
					"Authentication links",
					"Help desk",
					"Navigation bars",
				],
			},
		}

		if (planetData[this.currentPlanet]) {
			currentPlanetName = planetData[this.currentPlanet].name
			learntData = planetData[this.currentPlanet].learntData
			this.learntDataLength = learntData.length
		}

		// Title text
		// let headerText = this.add
		// 	.text(
		// 		this.gameW / 2,
		// 		50 * this.scaleFactor,
		// 		"Travelling At Hyper-Speed...",
		// 		{
		// 			fontSize: Math.round(this.fontSize + 12 * this.scaleFactor) + "px",
		// 			fill: "#fff",
		// 			fontFamily: '"Gluten", sans-serif',
		// 			fontWeight: "bold",
		// 			resolution: 2,
		// 		},
		// 	)
		// 	.setOrigin(0.5)

		// let headerText02 = this.add
		// 	.text(
		// 		this.gameW / 2,
		// 		90 * this.scaleFactor,
		// 		`On ${currentPlanetName}, you learnt about:`,
		// 		{
		// 			fontSize: Math.round(this.fontSize + 1 * this.scaleFactor) + "px",
		// 			fill: "#fff",
		// 			fontFamily: '"Gluten", sans-serif',
		// 			resolution: 2,
		// 		},
		// 	)
		// 	.setOrigin(0.5)

		// Define pill colors
		const pillColors = [0xbeccff, 0xfcb03a, 0xff66b9, 0x7ff9ff, 0xd8a5ff]

		// Set up pill dimensions and spacing - OPTIMIZED FOR SPACE USAGE
		const pillHeight = 38 * this.scaleFactor // Increased from 32 to use more space
		const pillPadding = 30 * this.scaleFactor // Slightly increased from 12
		const verticalSpacing = 16 * this.scaleFactor // Increased from 12 for better spacing
		const horizontalSpacing = 12 * this.scaleFactor // Increased from 10

		// Create container for pills
		const pillsContainer = this.add.container(0, 0)

		// Define proper container bounds with margins - OPTIMIZED FOR SPACE USAGE
		const margin = 100 * this.scaleFactor // Further reduced from 30
		const containerWidth = this.gameW - margin * 2
		const containerStartX = margin
		const containerStartY = this.gameH / 2 - 60 * this.scaleFactor // Moved up to use more space
		const maxHeight = this.gameH - containerStartY - 100 * this.scaleFactor // Reduced bottom margin

		// Starting position for pills
		let currentY = containerStartY
		let maxPillsHeight = 0

		// Track current row width
		let currentRowWidth = 0
		let currentRowItems = []

		// Create pills for each learnt item
		for (let i = 0; i < learntData.length; i++) {
			// Create text object to get its width - OPTIMIZED FONT SIZE
			const textObj = this.add.text(0, 0, learntData[i], {
				fontSize: Math.round(this.fontSize - 6 * this.scaleFactor) + "px", // Less reduction for better readability
				fill: "#fff",
				fontWeight: "500",
				fontFamily: '"Space Grotesk", sans-serif',
				resolution: 2,
			})

			// Calculate pill width based on text
			const pillWidth = textObj.width + pillPadding * 2

			// Check if we need to start a new row
			if (
				currentRowWidth > 0 &&
				currentRowWidth + pillWidth + horizontalSpacing > containerWidth
			) {
				// Center the items in this row
				this.centerRowItems(currentRowItems, currentRowWidth, this.gameW)

				// Start a new row
				currentY += pillHeight + verticalSpacing
				currentRowWidth = 0
				currentRowItems = []

				// Check if we've exceeded the available vertical space
				if (currentY + pillHeight > containerStartY + maxHeight) {
					// We need to adjust spacing or scale to fit everything
					this.adjustPillsToFit(learntData, containerStartY, maxHeight)

					// Exit the loop as we'll rebuild the pills with new dimensions
					textObj.destroy()
					break
				}
			}

			// Create the pill
			const colorIndex = i % pillColors.length
			const pill = this.createPill(
				0, // x will be set when centering row
				currentY,
				pillWidth,
				pillHeight,
				pillColors[colorIndex],
				learntData[i],
			)

			// Add to current row tracking
			currentRowWidth +=
				(currentRowWidth > 0 ? horizontalSpacing : 0) + pillWidth
			currentRowItems.push({
				obj: pill,
				width: pillWidth,
			})

			// Remove temporary text object
			textObj.destroy()
		}

		// Center the last row
		if (currentRowItems.length > 0) {
			this.centerRowItems(currentRowItems, currentRowWidth, this.gameW)
		}

		// Add NEXT button
		const nextButton = this.add
			.image(
				this.gameW - 100 * this.scaleFactor,
				this.gameH - 25 * this.scaleFactor,
				"saveBtn",
			)
			.setScale(this.scaleFactor)
			.setInteractive()
			.setAlpha(0.5)

		let nextButtonText = this.add
			.text(nextButton.x, nextButton.y - 8 * this.scaleFactor, "NEXT", {
				fontSize: Math.round(this.fontSize + 2 * this.scaleFactor) + "px",
				fill: "#fff",
				fontFamily: '"Gluten", sans-serif',
				fontStyle: "bold",
				fontWeight: "700",
				resolution: 2,
			})
			.setOrigin(0.5)
			.setAlpha(0.5)
		nextButton.on("pointerdown", () => {
			if (this.outcomesOpened <= 4) return; // Prevent proceeding until all outcomes opened
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			console.log("Current Planet: " + this.currentPlanet)

			this.outroGlossary()
			return
			if (this.currentPlanet === 3) {
				console.log("All 3 completed")
				this.scene.start("CertificatePresentation")
				return
			}
			console.log("Next Planet")
			this.scene.start("PlanetSelector")
		})

		this.nextButton = nextButton
		this.nextButtonText = nextButtonText

		updatePlanetData(this.currentPlanet)


		displayInstructions(
			this,
			"",
			12,
			500,
			80,
			60
		)

		let text01 = this.add
			.text(
				this.gameW / 2,
				50 * this.scaleFactor,
				"Press the learnings to read more. The more you tap, the better your progress!",
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 8 * this.scaleFactor,
					color: "#11044E",
					align: "center",
					fontStyle: "bold",
					fontWeight: "700",
					resolution: 2,
				}
			)
			.setOrigin(0.5)

		let text02 = this.add
			.text(
				this.gameW / 2,
				30 * this.scaleFactor,
				"What You Learnt",
				{
					fontFamily: "Gluten",
					fontSize: this.fontSize - 2 * this.scaleFactor,
					color: "#11044E",
					align: "center",
					fontStyle: "bold",
					fontWeight: "700",
					resolution: 2,
				}
			)
			.setOrigin(0.5)



		this.learningOutcomePopup()

		this.lOProgressBar = this.add.image(
			this.gameW / 2,
			this.gameH - 25 * this.scaleFactor,
			"lOProgressBar0",
		).setOrigin(0.5);
		this.lOProgressBar.setScale(this.scaleFactor * 0.5).setDepth(3)

		this.lOProgressFill = this.add.graphics()
		this.lOProgressFill.setPosition(
			this.lOProgressBar.x - this.lOProgressBar.width * this.scaleFactor * 0.5 / 2,
			this.lOProgressBar.y - this.lOProgressBar.height * this.scaleFactor * 0.5 / 2
		)
		this.lOProgressFill.setDepth(2);

		this.lOProgressBarMask = this.add.image(
			this.gameW / 2,
			this.gameH - 25 * this.scaleFactor,
			"lOProgressBar5",
		).setOrigin(0.5).setDepth(3);
		this.lOProgressBarMask.setScale(this.scaleFactor * 0.5).setVisible(false)

		this.lOProgressFill.mask = new Phaser.Display.Masks.BitmapMask(this, this.lOProgressBarMask)

		//this.outroGlossary()
		//this.areYouSurePopup()

		this.progressText = this.add
			.text(
				this.gameW / 2 - 150 * this.scaleFactor,
				this.gameH - 30 * this.scaleFactor,
				"Good!",
				{
					fontFamily: "Gluten",
					fontSize: this.fontSize - 7 * this.scaleFactor,
					color: "#11044E",
					align: "center",
					fontStyle: "bold",
					fontWeight: "700",
					resolution: 2,
				}
			)
			.setOrigin(0.5).setDepth(40000000).setVisible(false)

		this.starImage = this.add.image(
			this.lOProgressBar.x + 205 * this.scaleFactor,
			this.lOProgressBar.y - 10 * this.scaleFactor,
			"starImage")
			.setScale(this.scaleFactor * 0.5).setVisible(false).setDepth(4);
	}

	learningOutcomePopup() {
		this.lOGroup = this.add.group()
		this.lOOverlay = this.add.image(this.gameW / 2, this.gameH / 2, "lOOverlay")
		this.lOOverlay.setScale(this.scaleFactor * 2)
		// Make it interactive to block clicks
		this.lOOverlay.setInteractive()

		// Stop events from propagating to objects below
		this.lOOverlay.on("pointerdown", (pointer, localX, localY, event) => {
			event.stopPropagation()
		})
		this.lOBackground = this.add.image(this.gameW / 2, this.gameH / 2, "lOContainer")
		this.lOBackground.setScale(this.scaleFactor * 0.5)
		this.lOImage = this.add.image(this.lOBackground.x / 2 + 12 * this.scaleFactor, this.lOBackground.y - 6 * this.scaleFactor, "lOImageHolder")
		this.lOImage.setScale(this.scaleFactor * 0.5)

		this.lOVideoContainer = this.add.container(
			this.lOBackground.x / 2 + 12 * this.scaleFactor,
			this.lOBackground.y - 6 * this.scaleFactor
		)


		this.lOText = this.add
			.rexBBCodeText(
				this.lOBackground.x / 2 + 140 * this.scaleFactor,
				this.lOBackground.y - 90 * this.scaleFactor,
				"[b]A navigation bar[/b] is a user interface element that helps you move around a computer program or website. It typically contains links to different sections or pages, making it easy for you to navigate and find what you're looking for.\n\nWhen you click on a button in the navigation bar, it takes you to the corresponding page. Nav bars are often located at the top or bottom of an application window. Your Help Desk app has a nav bar to help you move between the sections.",
				{
					fontFamily: "Space Grotesk",
					fontSize: this.fontSize - 7 * this.scaleFactor,
					color: "#11044E",
					align: "left",
					wrap: {
						mode: "word",
						width: 360 * this.scaleFactor,
					},
					lineSpacing: 6,
					resolution: 2,
				},
			)
			.setOrigin(0, 0)

		this.lOCloseBtn = this.add
			.image(
				this.lOBackground.x / 2 + 490 * this.scaleFactor,
				this.lOBackground.y - 115 * this.scaleFactor,
				"closeButton",
			)
			.setScale(this.scaleFactor * 0.5)
			.setInteractive()

		this.lOCloseBtn.on("pointerup", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.5)
			this.lOGroup.setVisible(false)
			this.lOPill.destroy()
			this.lOPillText.destroy()

			// Stop and destroy current video if it exists
			if (this.currentVideo) {
				this.currentVideo.stop()
				this.currentVideo.destroy()
				this.currentVideo = null
				this.videoMask.destroy()
			}
		})

		this.lOGroup.add(this.lOOverlay)
		this.lOGroup.add(this.lOBackground)
		this.lOGroup.add(this.lOImage)
		this.lOGroup.add(this.lOVideoContainer)
		this.lOGroup.add(this.lOText)
		this.lOGroup.add(this.lOCloseBtn)
		this.lOGroup.setVisible(false)
	}

	loadAndDisplayVideo(learningOutcomeText) {
		const mediaData = this.videoMap[learningOutcomeText];

		if (!mediaData) {
			console.warn(`No media mapping found for: ${learningOutcomeText}`);
			return;
		}

		const { file: mediaKey, type: mediaType } = mediaData;

		// Stop and remove previous video if it exists
		if (this.currentVideo) {
			this.currentVideo.stop();
			this.currentVideo.destroy();
			this.currentVideo = null;
		}

		// Destroy video mask if it exists
		if (this.videoMask) {
			this.videoMask.destroy();
			this.videoMask = null;
		}

		// Destroy image mask if it exists
		if (this.imageMask) {
			this.imageMask.destroy();
			this.imageMask = null;
		}

		// Clear the video container
		this.lOVideoContainer.removeAll(true);

		if (mediaType === "image") {
			// Display image directly
			this.displayImage(mediaKey);
			console.log(`Displayed image for: ${learningOutcomeText} with key: ${mediaKey}`);
		} else {
			// Handle video loading and display with iOS detection
			// Use MP4 for iOS, WebM for others
			const videoExtension = this.isIOS ? 'mp4' : 'webm';
			const videoFileName = `${mediaKey}.${videoExtension}`;
			const videoPath = `assets/videos/${videoFileName}`;

			// Create a unique key for the video based on extension
			const videoKeyWithFormat = `${mediaKey}_${videoExtension}`;

			// Check if video is already loaded in the cache
			if (this.loadedVideos.has(videoKeyWithFormat)) {
				this.displayVideo(videoKeyWithFormat);
			} else {
				// Show loading indicator
				const loadingText = this.add.text(0, 0, "Loading...", {
					fontSize: this.fontSize + "px",
					fill: "#fff",
					fontFamily: '"Space Grotesk", sans-serif'
				}).setOrigin(0.5);
				this.lOVideoContainer.add(loadingText);

				// Load the video
				this.load.video(videoKeyWithFormat, videoPath, 'canplaythrough', false, false);

				this.load.once('complete', () => {
					this.loadedVideos.add(videoKeyWithFormat);
					loadingText.destroy();
					this.displayVideo(videoKeyWithFormat);
				});

				this.load.once('loaderror', (fileObj) => {
					console.error(`Failed to load video: ${videoFileName}`);
					loadingText.setText("Video unavailable");
					// Fallback to placeholder image
					setTimeout(() => {
						loadingText.destroy();
						const placeholderImage = this.add.image(0, 0, "lOImageHolder")
							.setScale(this.scaleFactor * 0.5);
						this.lOVideoContainer.add(placeholderImage);
					}, 1000);
				});

				this.load.start();
			}
		}
	}

	displayImage(imageKey) {
		const worldX = this.lOVideoContainer.x;
		const worldY = this.lOVideoContainer.y;

		// Create mask at the same world position as the container
		this.imageMask = this.add.image(
			worldX,
			worldY,
			"videoMask"
		).setVisible(false);

		// Create and add the image
		const image = this.add.image(0, 0, imageKey);

		// Scale the image to fit the container
		const targetWidth = 220 * this.scaleFactor;
		const targetHeight = 240 * this.scaleFactor;
		image.setDisplaySize(targetWidth, targetHeight);

		// Scale the mask to match - important!
		this.imageMask.setScale(this.scaleFactor * 0.492);

		// Apply the mask to the image
		image.mask = new Phaser.Display.Masks.BitmapMask(this, this.imageMask);

		this.lOVideoContainer.add(image);
	}

	displayVideo(videoKey) {
		// Create and add the video
		const worldX = this.lOVideoContainer.x;
		const worldY = this.lOVideoContainer.y;

		this.videoMask = this.add.image(
			worldX,
			worldY,
			"videoMask"
		).setVisible(false).setTint(0xfcb03a);

		this.currentVideo = this.add.video(0, 0, videoKey);

		// Scale the video to fit the container
		const targetWidth = 84 * this.scaleFactor;
		const targetHeight = 172 * this.scaleFactor;
		this.videoMask.setScale(this.scaleFactor * 0.492);
		this.currentVideo.setDisplaySize(targetWidth, targetHeight);

		this.lOVideoContainer.add(this.currentVideo);

		this.currentVideo.mask = new Phaser.Display.Masks.BitmapMask(this, this.videoMask);

		// Play the video and loop it
		this.currentVideo.play(true);
	}

	// Helper method to center row items
	centerRowItems(rowItems, totalRowWidth, gameWidth) {
		const startX = (gameWidth - totalRowWidth) / 2
		let currentX = startX

		rowItems.forEach((item) => {
			item.obj.x = currentX + item.width / 2
			currentX += item.width + 10 * this.scaleFactor // horizontal spacing
		})
	}

	adjustPillsToFit(learntData, startY, maxHeight) {
		// Clear any existing pills properly
		const containersToRemove = []

		this.children.list.forEach((child) => {
			if (
				child.type === "Container" &&
				child.list &&
				child.list.some((item) => item.type === "Graphics")
			) {
				containersToRemove.push(child)
			}
		})

		containersToRemove.forEach((container) => container.destroy())

		const initialScaleFactor = 1.1 // Increased from 1.0 to utilize space better

		this.createPillsLayout(learntData, startY, maxHeight, initialScaleFactor)
	}

	createPillsLayout(learntData, startY, maxHeight, scaleFactor = 1) {
		// Define a minimum acceptable scale factor for readability
		const minimumScaleFactor = 0.9

		// Set up pill dimensions - OPTIMIZED FOR SPACE USAGE
		const pillHeight = 40 * this.scaleFactor * scaleFactor // Increased from 35
		const pillPadding = 20 * this.scaleFactor * scaleFactor // Increased from 12
		const verticalSpacing = 18 * this.scaleFactor * scaleFactor // Increased from 15
		const horizontalSpacing = 14 * this.scaleFactor * scaleFactor // Increased from 12

		// Define container bounds with reduced margins
		const margin = 25 * this.scaleFactor // Further reduced from 30
		const containerWidth = this.gameW - margin * 2

		// Pre-calculate all pill widths to better plan layout
		const pillDimensions = learntData.map((item) => {
			// Optimized text size for better space usage
			const textSize = Math.round((this.fontSize - 5) * scaleFactor) + "px" // Less reduction for readability
			const textObj = this.add.text(0, 0, item, {
				fontSize: textSize,
				fill: "#fff",
				fontFamily: '"Space Grotesk", sans-serif',
				fontWeight: "600", // Make text slightly bolder
				resolution: 2,
			})

			const width = textObj.width + pillPadding * 2
			textObj.destroy()

			return { text: item, width }
		})

		// Create rows that respect the container width
		const rows = []
		let currentRow = []
		let rowWidth = 0

		pillDimensions.forEach((pill) => {
			const newWidth =
				rowWidth + pill.width + (currentRow.length > 0 ? horizontalSpacing : 0)

			if (newWidth <= containerWidth) {
				currentRow.push(pill)
				rowWidth = newWidth
			} else {
				rows.push([...currentRow])
				currentRow = [pill]
				rowWidth = pill.width
			}
		})

		if (currentRow.length > 0) {
			rows.push(currentRow)
		}

		// Calculate the total height needed
		const totalRowsHeight =
			rows.length * pillHeight + (rows.length - 1) * verticalSpacing

		// Check if we can fit all rows within the available height
		// But ensure we don't go below our minimum scale
		let finalScaleFactor = scaleFactor

		if (totalRowsHeight > maxHeight && scaleFactor > minimumScaleFactor) {
			// Calculate a new scale factor to fit the content
			const newScaleFactor = scaleFactor * (maxHeight / totalRowsHeight) * 0.95 // 0.95 for safety margin

			// Don't go below our minimum scale
			finalScaleFactor = Math.max(newScaleFactor, minimumScaleFactor)

			// If we need to scale down, start over with the new scale factor
			if (finalScaleFactor < scaleFactor) {
				return this.createPillsLayout(
					learntData,
					startY,
					maxHeight,
					finalScaleFactor,
				)
			}
		}

		// Distribute the vertical space better if we have few rows
		const pillColors = [0xbeccff, 0xfcb03a, 0xff66b9, 0x7ff9ff, 0xd8a5ff]

		// Add more vertical spacing if we have room - BETTER SPACE DISTRIBUTION
		let extraVerticalSpacing = 0
		// Reduce or skip extra spacing for Planet 2 with few rows
		if (this.currentPlanet === 2 && rows.length <= 2) {
			// No extra spacing for Planet 2 with 2 or fewer rows
			extraVerticalSpacing = 0
		} else if (rows.length <= 4 && totalRowsHeight < maxHeight * 0.85) {
			// Normal spacing distribution for other planets
			extraVerticalSpacing =
				(maxHeight * 0.85 - totalRowsHeight) / Math.max(1, rows.length - 1)
		}

		// create pills for each row
		let yPos = startY

		rows.forEach((row, rowIndex) => {
			let rowTotalWidth = 0

			// Calculate total width of this row including spacing
			row.forEach((pill, i) => {
				rowTotalWidth += pill.width
				if (i < row.length - 1) rowTotalWidth += horizontalSpacing
			})

			// Center this row
			let xPos = (this.gameW - rowTotalWidth) / 2

			// Create pills for this row
			row.forEach((pill, i) => {
				const colorIndex = (rowIndex + i) % pillColors.length // Vary colors by row+position
				const pillObj = this.createPill(
					xPos + pill.width / 2,
					yPos,
					pill.width,
					pillHeight,
					pillColors[colorIndex],
					pill.text,
					finalScaleFactor,
				)

				xPos += pill.width + horizontalSpacing
			})

			// Move to next row with potential extra spacing
			yPos +=
				pillHeight +
				verticalSpacing +
				(rowIndex < rows.length - 1 ? extraVerticalSpacing : 0)
		})
	}

	// Helper method to create a pill
	createPill(x, y, width, height, color, text, scaleFactor = 1) {

		// Create container for the pill
		const container = this.add.container(x, y)

		// Add background
		const bg = this.add.graphics()
		bg.lineStyle(3.5, color, 1)
		bg.strokeRoundedRect(-width / 2, -height / 2, width, height, height / 2)

		// Add subtle shadow or glow effect for better readability
		// const glow = this.add.graphics();
		// glow.lineStyle(5, 0xFFFFFF, 0.15);
		// glow.strokeRoundedRect(-width/2 + 2, -height/2 + 2, width - 4, height - 4, height/2 - 2);

		// Define the hit area for the graphics object
		bg.setInteractive(
			new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height),
			Phaser.Geom.Rectangle.Contains
		)

		container.add(bg)
		//container.add(glow);

		// Add text with enhanced readability and optimized font size
		const textObj = this.add
			.text(0, 0, text, {
				fontSize: Math.round((this.fontSize - 7) * scaleFactor) + "px", // Less reduction for better readability
				fill: "#fff",
				fontFamily: '"Space Grotesk", sans-serif',
				fontWeight: "500", // Make text bolder
				fontStyle: "bold",
				stroke: "#00000033", // Subtle text outline
				strokeThickness: 2, // Subtle outline thickness
				resolution: 2,
			})
			.setOrigin(0.5)
		container.add(textObj)

		bg.on("pointerdown", () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			if (bg._cooldown) return;
			bg._cooldown = true;
			this.time.delayedCall(1000, () => { bg._cooldown = false; });
			// Check if this outcome has already been opened
			if (this.openedOutcomes.has(text)) {
				// Just show the popup again, don't increment counter or modify pill
				this.lOGroup.setVisible(true);
				this.loadAndDisplayVideo(text);
				let pillXOffset = 140

				if (text === "Entering uppercase & lowercase letters") {
					if (this.sys.game.device.os.desktop || this.sys.game.device.os.laptop) {
						pillXOffset = 118
					}
				}
				// Recreate the pill display in popup
				this.lOPill = this.add.graphics();
				this.lOPill.fillStyle(color, 1);
				this.lOPill.fillRoundedRect(0, 0, width, height, height / 2);
				this.lOPill.lineStyle(3.5, color, 1);
				this.lOPill.strokeRoundedRect(0, 0, width, height, height / 2);
				this.lOPill.setPosition(this.lOBackground.x / 2 + pillXOffset * this.scaleFactor, this.lOBackground.y - 130 * this.scaleFactor);

				this.lOPillText = this.add.text(this.lOPill.x + width / 2, this.lOPill.y + height / 2, text, {
					fontSize: Math.round((this.fontSize - 7) * scaleFactor) + "px",
					fill: "#11044E", // Already black since it was opened
					fontFamily: '"Space Grotesk", sans-serif',
					fontWeight: "500",
					fontStyle: "bold",
					stroke: "#00000033",
					strokeThickness: 2,
					resolution: 2,
				}).setOrigin(0.5);

				const outcomeData = this.learningOutcomesData.find((item => item.id === text));
				if (outcomeData) {
					this.lOText.setText(outcomeData.outcome);
				} else {
					this.lOText.setText("Learning outcome not found.");
				}

				return; // Exit early, don't run the rest of the code
			}

			// Mark this outcome as opened
			this.openedOutcomes.add(text);
			this.outcomesOpened += 1;

			this.recordLearningOutcomeInteraction(text);

			if (this.outcomesOpened >= 5) {
				this.nextButton.setAlpha(1);
				this.nextButtonText.setAlpha(1);
			}

			// Progress bar animation section
			const progressWidth = this.lOProgressBar.width * this.scaleFactor;
			const progressHeight = this.lOProgressBar.height * this.scaleFactor * 0.5 - 10 * this.scaleFactor;
			const targetFillWidth = (this.outcomesOpened / this.learntDataLength) * progressWidth * 0.5;
			console.log(`Progress: ${this.outcomesOpened}/${this.learntDataLength}`);

			const planetThresholds = {
				1: { // Planet 1 - Total LO = 16
					good: 5,
					great: 8,
					super: 12,
					perfect: 15,
					star: 16
				},
				2: { // Planet 2 - Total LO = 11
					good: 3,
					great: 6,
					super: 8,
					perfect: 10,
					star: 11
				},
				3: { // Planet 3 - Total LO = 14
					good: 4,
					great: 7,
					super: 11,
					perfect: 13,
					star: 14
				}
			}

			// Get the thresholds for current planet
			const thresholds = planetThresholds[this.currentPlanet]

			// Progress text logic - place this in your pill click handler
			if (this.outcomesOpened === thresholds.good) {
				this.progressText.setVisible(true)
				this.progressText.setText("Good!")
				this.progressText.setPosition(
					this.gameW / 2 - 150 * this.scaleFactor,
					this.gameH - 30 * this.scaleFactor
				)
			}

			if (this.outcomesOpened === thresholds.great) {
				this.progressText.setText("Great!")
				this.progressText.setPosition(
					this.gameW / 2 - 150 * this.scaleFactor + 100 * this.scaleFactor,
					this.gameH - 30 * this.scaleFactor
				)
			}

			if (this.outcomesOpened === thresholds.super) {
				this.progressText.setText("Super!")
				this.progressText.setPosition(
					this.gameW / 2 - 150 * this.scaleFactor + 200 * this.scaleFactor,
					this.gameH - 30 * this.scaleFactor
				)
			}

			if (this.outcomesOpened === thresholds.perfect) {
				this.progressText.setText("Perfect!")
				//this.starImage.setVisible(true)
				this.progressText.setPosition(
					this.gameW / 2 - 150 * this.scaleFactor + 300 * this.scaleFactor,
					this.gameH - 30 * this.scaleFactor
				)
				playExplosionAnimation(this, {
					x: this.lOProgressBar.x / 2 + this.lOProgressBar.width / 2 * this.scaleFactor,
					y: this.lOProgressBar.y - 10 * this.scaleFactor,
				})
			}

			if (this.outcomesOpened === thresholds.star) {
				// Final star transformation
				this.starImage.setVisible(true)
				// this.starImage.setPosition(
				// 	this.lOProgressBar.x + 50 * this.scaleFactor,
				// 	this.lOProgressBar.y - 10 * this.scaleFactor
				// )
				playExplosionAnimation(this, {
					x: this.lOProgressBar.x / 2 + this.lOProgressBar.width / 2 * this.scaleFactor,
					y: this.lOProgressBar.y - 10 * this.scaleFactor,
				})
			}

			const colors = [
				0x7FF9FF,
				0xBECCFF,
				0xAF67DB,
				0xFCB03A,
			];

			const getColorAtProgress = (progress) => {
				const colorProgress = progress * (colors.length - 1);
				const currentColorIndex = Math.floor(colorProgress);
				const nextColorIndex = Math.min(currentColorIndex + 1, colors.length - 1);
				const colorLerp = colorProgress - currentColorIndex;

				const currentColor = Phaser.Display.Color.ValueToColor(colors[currentColorIndex]);
				const nextColor = Phaser.Display.Color.ValueToColor(colors[nextColorIndex]);
				const interpolatedColor = Phaser.Display.Color.Interpolate.ColorWithColor(
					currentColor,
					nextColor,
					100,
					colorLerp * 100
				);

				return Phaser.Display.Color.GetColor(
					interpolatedColor.r,
					interpolatedColor.g,
					interpolatedColor.b
				);
			};

			// Animate the progress bar fill
			this.tweens.add({
				targets: { width: this.lOProgressFill.width || 0 },
				width: targetFillWidth,
				duration: 600,
				ease: 'Cubic.easeOut',
				onUpdate: (tween) => {
					const currentWidth = tween.getValue();
					const currentProgress = this.outcomesOpened / this.learntDataLength;

					this.lOProgressFill.clear();
					this.lOProgressFill.fillStyle(getColorAtProgress(currentProgress), 1);
					this.lOProgressFill.fillRoundedRect(
						0,
						0,
						currentWidth,
						progressHeight,
						progressHeight / 2
					);

					this.lOProgressFill.width = currentWidth;
				}
			});

			// Add extra space to the pill width for the image
			const imageSize = height * 0.6;
			const imagePadding = height * 0.2;
			const extraWidth = 10 * this.scaleFactor;

			// Update the pill width
			const newWidth = width + extraWidth;

			// Redraw the pill with new width
			bg.clear();
			bg.fillStyle(color, 1);
			bg.fillRoundedRect(-newWidth / 2, -height / 2, newWidth, height, height / 2);
			bg.lineStyle(3.5, color, 1);
			bg.strokeRoundedRect(-newWidth / 2, -height / 2, newWidth, height, height / 2);

			bg.setInteractive(
				new Phaser.Geom.Rectangle(-newWidth / 2, -height / 2, newWidth, height),
				Phaser.Geom.Rectangle.Contains
			);

			// Position the bgClicked image on the left side of the expanded pill
			const bgClickedX = -newWidth / 2 + imagePadding / 2 + imageSize / 2;

			let bgClicked = this.add.image(bgClickedX, 0, "lOOpened")
				.setOrigin(0.5, 0.5)
				.setScale(scaleFactor * 0.5)
				.setDisplaySize(imageSize, imageSize);

			container.add(bgClicked);

			console.log(`Pill clicked: ${text}`);

			// Shift the text slightly to the right to center it with the new image
			textObj.x = extraWidth / 2;
			textObj.setColor("#11044E");

			// Update the interactive hit area to match new size
			// bg.removeInteractive();
			// bg.setInteractive(
			// 	new Phaser.Geom.Rectangle(-newWidth / 2, -height / 2, newWidth, height),
			// 	Phaser.Geom.Rectangle.Contains
			// );

			this.lOGroup.setVisible(true);
			let pillXOffset = 140

			if (text === "Entering uppercase & lowercase letters") {
				if (this.sys.game.device.os.desktop || this.sys.game.device.os.laptop) {
					pillXOffset = 118
				}
			}

			this.lOPill = this.add.graphics();
			this.lOPill.fillStyle(color, 1);
			this.lOPill.fillRoundedRect(0, 0, width, height, height / 2);
			this.lOPill.lineStyle(3.5, color, 1);
			this.lOPill.strokeRoundedRect(0, 0, width, height, height / 2);
			this.lOPill.setPosition(this.lOBackground.x / 2 + pillXOffset * this.scaleFactor, this.lOBackground.y - 135 * this.scaleFactor);

			this.lOPillText = this.add.text(this.lOPill.x + width / 2, this.lOPill.y + height / 2, text, {
				fontSize: Math.round((this.fontSize - 7) * scaleFactor) + "px",
				fill: "#fff",
				fontFamily: '"Space Grotesk", sans-serif',
				fontWeight: "500",
				fontStyle: "bold",
				stroke: "#00000033",
				strokeThickness: 2,
				resolution: 2,
			}).setOrigin(0.5);

			this.lOPillText.setColor("#11044E");

			this.loadAndDisplayVideo(text);

			const outcomeData = this.learningOutcomesData.find((item => item.id === text));

			if (outcomeData) {
				this.lOText.setText(outcomeData.outcome);
			} else {
				this.lOText.setText("Learning outcome not found.");
			}
		});

		return container;
	}

	outroGlossary() {

		this.progressText.setVisible(false)

		let inputBlocker = this.add.rectangle(0, 0, this.gameW, this.gameH, 0x000000, 0.5).setOrigin(0).setInteractive().setDepth(50)
		console.log("currentPlanet:" + this.currentPlanet)
		const planetNames = {
			1: "Hypotherra",
			2: "Stratosoma",
			3: "Emberon"
		};
		const planetName = planetNames[this.currentPlanet];
		let container = this.add.image(this.gameW / 2, this.gameH / 2, "outroGlossary").setScale(this.scaleFactor * 0.5).setOrigin(0.5).setDepth(50)
		let planetCompleteText = this.add.text(
			container.x,
			container.y - 160 * this.scaleFactor,
			`${planetName} Complete!`,
			{
				fontSize: Math.round(this.fontSize + 1 * this.scaleFactor) + "px",
				fill: "#11044E",
				fontFamily: '"Gluten", sans-serif',
				fontStyle: "bold",
				fontWeight: "700",
				resolution: 2,
			}
		).setOrigin(0.5).setDepth(50)

		let whatNextText = this.add.text(
			planetCompleteText.x,
			planetCompleteText.y + 25 * this.scaleFactor,
			`What would you like to do next?`, {
			fontSize: Math.round(this.fontSize - 6 * this.scaleFactor) + "px",
			fill: "#11044E",
			fontFamily: '"Space Grotesk", sans-serif',
			fontWeight: "500",
			resolution: 2,
		}).setOrigin(0.5).setDepth(50)

		let planetXOffset = 130 * this.scaleFactor
		let planetYOffset = -30 * this.scaleFactor

		let prevPlanet = this.add.image(
			container.x - planetXOffset,
			container.y + planetYOffset,
			"planet0" + this.currentPlanet
		).setScale(this.scaleFactor * 0.5).setDepth(50)

		let prevPlanetComplete = this.add.image(
			prevPlanet.x,
			prevPlanet.y + 0.1 * this.scaleFactor,
			"planetComplete"
		).setScale(0.5 * this.scaleFactor).setDepth(50)

		let nextPlanet = this.add.image(
			container.x + planetXOffset,
			container.y + planetYOffset,
			"planet0" + Math.min(this.currentPlanet + 1, 3)
		).setScale(this.scaleFactor * 0.5).setDepth(50)

		let textYOffset = 80 * this.scaleFactor

		let replayText = this.add.rexBBCodeText(
			prevPlanet.x,
			prevPlanet.y + textYOffset,
			"[b]Replay[/b] this planet", {
			fontSize: Math.round(this.fontSize - 6 * this.scaleFactor) + "px",
			fill: "#11044E",
			fontFamily: '"Space Grotesk", sans-serif',
			fontWeight: "500",
			align: "center",
			resolution: 2,
		}).setOrigin(0.5).setDepth(50)

		let nextPlanetText = this.add.rexBBCodeText(
			nextPlanet.x,
			nextPlanet.y + textYOffset,
			`Go on to the [b]next planet![/b]`, {
			fontSize: Math.round(this.fontSize - 6 * this.scaleFactor) + "px",
			fill: "#11044E",
			fontFamily: '"Space Grotesk", sans-serif',
			fontWeight: "500",
			align: "center",
			resolution: 2,
		}).setOrigin(0.5).setDepth(50)

		let replayButton = this.add.image(
			replayText.x,
			replayText.y + 50 * this.scaleFactor,
			'replayButton'
		).setScale(this.scaleFactor * 0.5).setInteractive().setDepth(50)

		let replayButtonText = this.add.text(
			replayButton.x + 10 * this.scaleFactor,
			replayButton.y - 7 * this.scaleFactor,
			"REPLAY",
			{
				fontSize: Math.round(this.fontSize + 2 * this.scaleFactor) + "px",
				fill: "#fff",
				fontFamily: '"Gluten", sans-serif',
				fontStyle: "bold",
				fontWeight: "700",
				resolution: 2,
			}
		).setOrigin(0.5).setDepth(50)

		let continueButton = this.add.image(
			nextPlanetText.x,
			nextPlanetText.y + 50 * this.scaleFactor,
			'continueButton'
		).setScale(this.scaleFactor * 0.5).setInteractive().setDepth(50)

		let continueButtonText = this.add.text(
			continueButton.x + 10 * this.scaleFactor,
			continueButton.y - 7 * this.scaleFactor,
			"CONTINUE",
			{
				fontSize: Math.round(this.fontSize + 2 * this.scaleFactor) + "px",
				fill: "#fff",
				fontFamily: '"Gluten", sans-serif',
				fontStyle: "bold",
				fontWeight: "700",
				resolution: 2,
			}
		).setOrigin(0.5).setDepth(50)

		if (this.currentPlanet === 3) {
			nextPlanetText.setText(`Finish & replay [b]from the start![/b]`)
			continueButtonText.setText("FINISH")
			nextPlanet.setTexture("planet01")
		}

		replayButton.on('pointerdown', () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			this.areYouSurePopup()
		})

		continueButton.on('pointerdown', () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			if (this.currentPlanet === 3) {
				this.scene.start("CertificatePresentation")
			} else {
				this.scene.start("PlanetSelector")
			}
		})

		animateElements([
			container,
			planetCompleteText,
			whatNextText,
			prevPlanet,
			prevPlanetComplete,
			nextPlanet,
			replayText,
			nextPlanetText,
			replayButton,
			replayButtonText,
			continueButton,
			continueButtonText
		], this)
	}

	areYouSurePopup() {

		let inputBlocker = this.add.rectangle(0, 0, this.gameW, this.gameH, 0x000000, 0.5).setOrigin(0).setInteractive().setDepth(50)
		let container = this.add.image(this.gameW / 2, this.gameH / 2, "rUSurePanel").setScale(this.scaleFactor * 0.5).setOrigin(0.5).setDepth(50)
		let rUSureText = this.add.text(
			container.x,
			container.y - 30 * this.scaleFactor,
			"Are you sure?", {
			fontSize: Math.round(this.fontSize + 1 * this.scaleFactor) + "px",
			fill: "#11044E",
			fontFamily: '"Gluten", sans-serif',
			fontWeight: "700",
			fontStyle: "bold",
			align: "center",
			resolution: 2,
		}).setOrigin(0.5).setDepth(50)

		let text = this.add.rexBBCodeText(
			rUSureText.x,
			rUSureText.y + 30 * this.scaleFactor,
			`Replaying this planet will [b]reset your progress[/b] for this planet.`,
			{
				fontFamily: "Space Grotesk",
				fontSize: this.fontSize - 6 * this.scaleFactor,
				color: "#11044E",
				align: "center",
				lineSpacing: 4,
				resolution: 2,
			}
		).setOrigin(0.5).setDepth(50)

		let yesButton = this.add.image(
			container.x - 70 * this.scaleFactor,
			container.y + 55 * this.scaleFactor,
			'okBtn'
		).setScale(this.scaleFactor).setInteractive().setDepth(50)

		let yesButtonText = this.add.text(
			yesButton.x + 0.1 * this.scaleFactor,
			yesButton.y - 7 * this.scaleFactor,
			"YES",
			{
				fontSize: Math.round(this.fontSize + 2 * this.scaleFactor) + "px",
				fill: "#fff",
				fontFamily: '"Gluten", sans-serif',
				fontStyle: "bold",
				fontWeight: "700",
				resolution: 2,
			}
		).setOrigin(0.5).setDepth(50)

		let cancelButton = this.add.image(
			container.x + 70 * this.scaleFactor,
			container.y + 55 * this.scaleFactor,
			'normalWideBtn'
		).setScale(this.scaleFactor * 0.5).setInteractive().setDepth(50)

		let cancelButtonText = this.add.text(
			cancelButton.x + 0.1 * this.scaleFactor,
			cancelButton.y - 7 * this.scaleFactor,
			"CANCEL",
			{
				fontSize: Math.round(this.fontSize + 2 * this.scaleFactor) + "px",
				fill: "#fff",
				fontFamily: '"Gluten", sans-serif',
				fontStyle: "bold",
				fontWeight: "700",
				resolution: 2,
			}
		).setOrigin(0.5).setDepth(50)

		yesButton.on('pointerdown', () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			this.scene.start('landingScene')
		})

		cancelButton.on('pointerdown', () => {
			playUISound(this, this.soundManager, "Subtle click 1", 0.25)
			inputBlocker.destroy()
			container.destroy()
			rUSureText.destroy()
			text.destroy()
			yesButton.destroy()
			yesButtonText.destroy()
			cancelButton.destroy()
			cancelButtonText.destroy()
		})

		animateElements([
			container,
			rUSureText,
			text,
			yesButton,
			yesButtonText,
			cancelButton,
			cancelButtonText
		], this)
	}

	//GAME ANALYTICS FOR LEVEL/COMPLETED PLANETS
	//ADD TIME SPENT PER LEVEL
	sendAnalyticsEvent() {
		const endTimestamp = Date.now()
		const readableDate = new Date(endTimestamp)
			.toISOString()
			.split("T")[0]
			.replace(/-/g, "")

		const startTimestampStr = localStorage.getItem("levelStartTime")
		const startTimestamp = startTimestampStr
			? parseInt(startTimestampStr)
			: null

		let completionTimeSec = 0

		if (startTimestamp) {
			completionTimeSec = Math.floor((endTimestamp - startTimestamp) / 1000)

			GameAnalytics.addProgressionEvent(
				"Complete",
				`planet_${this.currentPlanet}`,
			)

			GameAnalytics.addDesignEvent(`session_end`, completionTimeSec)

			localStorage.removeItem("levelStartTime")
		} else {
			console.warn("No level start timestamp found for duration calculation.")

			GameAnalytics.addProgressionEvent(
				"Complete",
				`planet_${this.currentPlanet}`,
			)

			GameAnalytics.addDesignEvent(`session_end`, completionTimeSec)
		}

		this.recordAllLearningOutcomes();
	}

	async learningOutcomesPressed() {

	}

	async levelCompleteMileStone(planet) {
		const milestoneMap = {
			1: ["C2.5.3", "C2.1.1", "C2.3.1"],
			2: ["C1.2.3", "C1.1.5", "C3.3.3", "C1.3.2", "C1.2.4", "C1.3.1"],
			3: [
				"C1.2.6",
				"C2.2.5",
				"C4.1.4",
				"C3.2.4",
				"C1.5.4",
				"C1.1.2",
				"C1.1.1",
				"C4.3.2",
				"C4.3.3",
				"C2.6.1",
				"C3.3.2",
			],
		}

		try {
			const codes = milestoneMap[this.currentPlanet]
			if (codes && Array.isArray(codes)) {
				for (const code of codes) {
					await this.apiHandler.recordMilestoneCompletion(this.userID, code)
				}
			}
		} catch (error) {
			console.error("Error recording milestone(s):", error)
		}
	}

	async recordLearningOutcomeInteraction(learningOutcomeName) {
		try {
			// Record the learning outcome interaction with GameAnalytics
			GameAnalytics.addDesignEvent(
				`learning_outcome:${learningOutcomeName}`,
				1
			)

			// You can also track it per planet if needed
			GameAnalytics.addDesignEvent(
				`planet_${this.currentPlanet}:learning_outcome:${learningOutcomeName}`,
				1
			)

			console.log(`Analytics: Learning outcome "${learningOutcomeName}" recorded for planet ${this.currentPlanet}`)
		} catch (error) {
			console.error("Error recording learning outcome analytics:", error)
		}
	}

	async recordAllLearningOutcomes() {
		try {
			// Convert the Set to an Array for easier processing
			const pressedOutcomes = Array.from(this.openedOutcomes);

			// Send a summary event
			GameAnalytics.addDesignEvent(
				`planet_${this.currentPlanet}:total_outcomes_pressed`,
				pressedOutcomes.length
			);

			// Send individual events for each pressed outcome
			for (const outcome of pressedOutcomes) {
				GameAnalytics.addDesignEvent(
					`planet_${this.currentPlanet}:completed_outcome:${outcome}`,
					1
				);
			}

			console.log(`Analytics: ${pressedOutcomes.length} learning outcomes recorded for planet ${this.currentPlanet}`);
		} catch (error) {
			console.error("Error recording learning outcomes summary:", error);
		}
	}

	async playerGameRating() {
		try {
			await this.apiHandler.recordRating(this.userID, 5, "good work")
		} catch (error) {
			console.error("Error recording milestone(s):", error)
		}
	}
}